import React, { useContext } from 'react'
import classNames from 'classnames'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import MainNavigationContext from '@toasttab/shared/src/data/HeaderNavigation/context/MainNavigationContext'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import {
  ShowcaseEntryProps,
  SubMenuProps,
  SubMenuEntryProps
} from '@toasttab/shared/src/data/HeaderNavigation/MainNavigationMenu.d'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { I18NextContext } from '@toasttab/shared/src/@types/types'

const generateSubMenuSectionShowcases = (
  showcase: ShowcaseEntryProps,
  localizationContext: I18NextContext
) => {
  const showcaseHref = localizeLink(
    showcase.url,
    localizationContext.language,
    localizationContext
  )

  return (
    <div
      className='text-secondary font-sansPro mb-6 p-2 w-[170px] min-[1440px]:w-[360px] rounded-lg bg-white bg-white group'
      role='none'
      key={`desktop-sub-menu-showcase-${showcase.title}}`}
    >
      <a
        className={`${showcase.heapClass} flex flex-nowrap items-center group`}
        role='menuitem'
        href={showcaseHref}
      >
        <img
          className='w-[128px] h-[112px] hidden min-[1440px]:block rounded-lg mr-6'
          src={showcase.thumbnail}
          alt={`${showcase.title} Thumbnail`}
        />

        <div className='w-full'>
          {showcase.badge && (
            <div className='w-full'>
              <Badge
                badgeColor={showcase.badge.color}
                classes='text-[10px] leading-[14px] tracking-[1px] py-[2px] mb-2'
                text={showcase.badge.label}
                uppercase
              />
            </div>
          )}
          <p className='font-sansPro text-base text-default group-hover:text-blue-75 group-focus:text-blue-75 tracking-normal mb-2 leading-none'>
            {showcase.title}
            <span className='invisible inline-flex relative left-[0.625rem] top-[0.375rem] h-5 w-5 content-icon-right-arrow-blue group-hover:visible group-focus:visible' />
          </p>
          <p className='font-sansPro font-normal text-xs text-secondary'>
            {showcase.description}
          </p>
        </div>
      </a>
    </div>
  )
}

const generateEntryType = (
  parent: string,
  menu: SubMenuProps,
  localizationContext: I18NextContext
) => {
  const twoCol: boolean = menu.subMenuEntries.length > 7
  const entryType: string = menu.subMenuEntryType

  const sectionHeadingCss = classNames({
    'font-sansPro font-normal text-base text-secondary border-b border-gray-25 pb-2 mb-4':
      true,
    'group-hover:text-blue-75 group-focus:text-blue-75':
      entryType === 'articles'
  })

  const sectionListCss = classNames({
    flex: true,
    'flex-col flex-nowrap': menu.subMenuEntries.length <= 7,
    'flex-row flex-wrap': menu.subMenuEntries.length > 7
  })

  if (entryType === 'articles') {
    const articleHref = localizeLink(
      menu.subMenuEntries[0].url,
      localizationContext.language,
      localizationContext
    )
    return (
      <div
        className={
          entryType === 'articles' &&
          'p-1.5 rounded-lg group-hover:bg-gray-0 group-focus:bg-gray-0'
        }
        key={`desktop-sub-menu-section-${menu.label}}`}
      >
        <a
          className={`${menu.subMenuEntries[0].heapClass} group`}
          role='menuitem'
          href={articleHref}
        >
          <h5 className={`${sectionHeadingCss} group`}>
            {menu.label}
            <span className='inline-flex invisible relative left-[0.625rem] top-2 content-icon-right-arrow-blue h-6 w-6 group-hover:visible group-focus:visible' />
          </h5>
          <ul className={sectionListCss}>
            {menu.subMenuEntries.map((menu) =>
              generateSubMenuSectionEntries(
                parent,
                menu,
                entryType,
                twoCol,
                localizationContext
              )
            )}
          </ul>
        </a>
      </div>
    )
  } else {
    return (
      <div key={`desktop-sub-menu-section-${menu.label}}`}>
        <h5 className={sectionHeadingCss}>{menu.label}</h5>
        <ul className={sectionListCss}>
          {menu.subMenuEntries.map((menu) =>
            generateSubMenuSectionEntries(
              parent,
              menu,
              entryType,
              twoCol,
              localizationContext
            )
          )}
        </ul>
      </div>
    )
  }
}

const generateSubMenuSectionEntries = (
  parent: string,
  menu: SubMenuEntryProps,
  entryType: string,
  twoCol: boolean,
  localizationContext: I18NextContext
) => {
  const isArticle = Boolean(entryType === 'articles')
  const listItemCss = classNames({
    'p-1.5 rounded-lg hover:bg-gray-0 focus:bg-gray-0 group':
      entryType !== 'articles',
    'basis-1/2 [&>*:nth-child(-n + 5)]:order-[1] [&>*:nth-child(n + 6)]:order-[2]':
      twoCol
  })

  const linkCss = classNames('font-normal text-default', {
    'inline-block w-full group-hover:text-blue-75 group-focus:text-blue-75':
      entryType !== 'articles'
  })

  const linkHref = localizeLink(
    menu.url,
    localizationContext.language,
    localizationContext
  )

  // Using "null" as the variant label text as a means to hide the link for the test
  return (
    <li
      className={listItemCss}
      role='none'
      key={`desktop-sub-menu-link-${menu.heapClass}}`}
    >
      {isArticle ? (
        <span className={`${menu.heapClass} ${linkCss}`} role='menuitem'>
          {menu.label}
        </span>
      ) : (
        <a
          className={`${menu.heapClass} ${linkCss}`}
          role='menuitem'
          href={linkHref}
        >
          {menu.label}
        </a>
      )}
    </li>
  )
}

const generateSubMenuSections = (
  parent: string,
  menu: SubMenuProps,
  localizationContext: I18NextContext
) => {
  const entryType: string = menu.subMenuEntryType

  const submenuSectionCss = classNames({
    'col-span-2': menu.subMenuEntries.length <= 7 && entryType !== 'articles',
    'col-span-4': menu.subMenuEntries.length > 7 && entryType !== 'articles',
    'col-span-3 min-[1440px]:col-span-2 group': entryType === 'articles'
  })

  return (
    <div
      className={submenuSectionCss}
      key={`desktop-sub-menu-section-${menu.subMenuEntries[0].label}}`}
    >
      {generateEntryType(parent, menu, localizationContext)}
    </div>
  )
}

export const SubMenus = ({ menu }: any) => {
  const { selectedParentMenu, setSelectedParentMenu } = useContext(
    MainNavigationContext
  )
  const localizationContext = useContext(I18nextContext)
  const windowSize = useWindowSize()

  const subWrapperCss = (parent: string, selectedParentMenu: string) => {
    return classNames('absolute left-0 w-screen h-screen top-[72px]', {
      hidden: parent !== selectedParentMenu || windowSize.width < 992
    })
  }

  const getSubMenuCss = (parent: string, selectedParentMenu: string) => {
    return classNames('max-h-[calc(100vh-72px)]', {
      hidden: parent !== selectedParentMenu || windowSize.width < 992,
      'flex fixed z-[90] bg-white w-full rounded-b-lg drop-shadow-lg animate-fade-from-top':
        parent === selectedParentMenu && windowSize.width >= 992
    })
  }

  const getOverlayCss = (parent: string, selectedParentMenu: string) => {
    return classNames({
      overlay: true,
      hidden: parent !== selectedParentMenu || windowSize.width < 992,
      'fixed bg-black opacity-30 w-full h-full z-[80]':
        parent === selectedParentMenu && windowSize.width >= 992
    })
  }

  // Allow customization of grid columns for international navs
  const subMenuClasses = classNames(
    'grid gap-6 bg-white py-9 pl-12 pr-7 min-[1440px]:pr-[156px] w-full rounded-br-lg overflow-y-scroll !no-scrollbar',
    {
      'grid-cols-6': !menu.gridCols,
      [menu.gridCols]: menu.gridCols
    }
  )

  const parent = menu.label

  return (
    <div
      id={`${menu.label.replace(/\s/g, '-')}-menu`}
      className={subWrapperCss(menu.label, selectedParentMenu)}
      key={`desktop-sub-menu-${menu.label}}`}
    >
      <nav
        className={getSubMenuCss(menu.label, selectedParentMenu)}
        aria-label={`Toast ${menu.label} Dropdown Menu`}
        key={`desktop-sub-menu-${menu.label}}`}
      >
        {menu?.showcaseEntries && (
          <div className='bg-gray-0 pl-7 min-[1440px]:pl-[156px] pr-12 py-9 min-w-[242px] min-[1440px]:min-w-[564px] rounded-b-lg rounded-tr-lg overflow-y-scroll !no-scrollbar'>
            {menu.showcaseEntries.map((showcase: any) =>
              generateSubMenuSectionShowcases(showcase, localizationContext)
            )}
          </div>
        )}
        <div className={subMenuClasses}>
          {menu.subMenus.map((menu: any) =>
            generateSubMenuSections(parent, menu, localizationContext)
          )}
          {menu.sectionLink && (
            <div className='col-[1_/_-1] text-right'>
              <TextLink
                href={localizeLink(
                  menu.sectionLink.url,
                  localizationContext.language,
                  localizationContext
                )}
                icon='arrow'
                text={menu.sectionLink.label}
              />
            </div>
          )}
        </div>
      </nav>
      <div
        className={getOverlayCss(menu.label, selectedParentMenu)}
        onMouseEnter={() => setSelectedParentMenu(null)}
      />
    </div>
  )
}
